// src/components/core/DocumentIdentifier.jsx
// *******************************************************************************
// DOCUMENT IDENTIFIER COMPONENT
// *******************************************************************************
// Reusable component to display document _id and legacyCode with copy functionality.
// Shows: "_id: [copy icon] 6970ef30757b37ea2b096656 | LC: [copy icon] 45443"
// The "| LC: ..." part only appears if legacyCode exists.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Tooltip } from 'antd';
import appService from 'services/appService';
import translateX from 'utils/translateX';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DocumentIdentifier = ({ data, style = {}, wrap = false }) => {
  const documentId = data?._id;
  const legacyCode = data?.legacyCode;
  const documentCode = data?._metadata?.__documentCode;

  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  // Format legacyCode for display (handle array or string)
  const formattedLegacyCode = Array.isArray(legacyCode) ? legacyCode.join(', ') : legacyCode;
  const hasLegacyCode = legacyCode && (Array.isArray(legacyCode) ? legacyCode.length > 0 : true);

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleCopyId = () => {
    appService.handleCopyToClipboard(documentId, 'document_id_copied');
  };

  const handleCopyDocumentCode = () => {
    appService.handleCopyToClipboard(documentCode, 'document_code_copied');
  };

  const handleCopyLegacyCode = () => {
    appService.handleCopyToClipboard(formattedLegacyCode, 'legacy_code_copied');
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!documentId) return null;

  return (
    <div
      className="text-muted"
      style={{
        marginTop: 8,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 16,
        fontSize: 10,
        ...(wrap && { flexWrap: 'wrap', rowGap: 8 }),
        ...style,
      }}
    >
      {/* Document ID */}
      <Tooltip title={translateX('copy_internal_id')}>
        <span style={{ cursor: 'pointer' }} onClick={handleCopyId}>
          <strong>ID: </strong>
          {documentId}
        </span>
      </Tooltip>

      {/* Document Code */}
      {documentCode ? (
        <Tooltip title={translateX('copy_document_code')}>
          <span style={{ cursor: 'pointer' }} onClick={handleCopyDocumentCode}>
            <strong>DC: </strong>
            {documentCode}
          </span>
        </Tooltip>
      ) : null}

      {/* Legacy Code (only if exists) */}
      {hasLegacyCode && (
        <Tooltip title={translateX('copy_legacy_code')}>
          <span style={{ cursor: 'pointer' }} onClick={handleCopyLegacyCode}>
            <strong>LC: </strong>
            {formattedLegacyCode}
          </span>
        </Tooltip>
      )}
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DocumentIdentifier;
